import React from "react";
import DashboardLayout from "./Layouts/DashboardLayout";
import { AiOutlineMail, AiOutlineLink } from "react-icons/ai";
import { FaFacebook, FaWhatsapp, FaInstagram } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import referAndEarnImage from "../assets/referandearn.png";

import logger from '../utils/logger';
const fadeInAnimation = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export default function ReferEarn() {
  const [showShareMenu, setShowShareMenu] = React.useState(false);
  const [referralLink, setReferralLink] = React.useState("https://visibeen.com/ref?code=VB2024");
  const [isEditable, setIsEditable] = React.useState(false);
  
  const handleShare = (platform) => {
    logger.log('handleShare called with platform:', platform);
    
    if (!referralLink) {
      toast.error("Please enter a referral link first");
      return;
    }

    try {
      switch (platform) {
        case 'email':
          window.location.href = `mailto:?subject=Join Visibeen&body=Check out Visibeen using my referral link: ${referralLink}`;
          logger.log('Email share triggered');
          break;
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank');
          logger.log('Facebook share triggered');
          break;
        case 'whatsapp':
          window.open(`https://api.whatsapp.com/send?text=Check out Visibeen using my referral link: ${encodeURIComponent(referralLink)}`, '_blank');
          logger.log('WhatsApp share triggered');
          break;
        case 'instagram':
          // Instagram doesn't have a direct share URL, so we'll copy to clipboard and notify user
          navigator.clipboard.writeText(referralLink)
            .then(() => {
              logger.log('Text copied to clipboard for Instagram');
              toast.info("Link copied to clipboard. Open Instagram to share!");
            })
            .catch(err => {
              logger.error('Failed to copy text: ', err);
              toast.error("Failed to copy link. Please try again.");
            });
          break;
        case 'copy':
          navigator.clipboard.writeText(referralLink)
            .then(() => {
              logger.log('Text copied to clipboard');
              toast.success("Referral link copied to clipboard!");
            })
            .catch(err => {
              logger.error('Failed to copy text: ', err);
              toast.error("Failed to copy link. Please try again.");
            });
          break;
        default:
          logger.log('Unknown platform:', platform);
          break;
      }
    } catch (error) {
      logger.error('Error in handleShare:', error);
      toast.error("An error occurred. Please try again.");
    }
    
    // Close the share menu after sharing
    setShowShareMenu(false);
  };

  const shareMenuRef = React.useRef(null);
  const shareBtnRef = React.useRef(null);

  // Close share menu when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        shareMenuRef.current &&
        !shareMenuRef.current.contains(event.target) &&
        shareBtnRef.current &&
        !shareBtnRef.current.contains(event.target)
      ) {
        setShowShareMenu(false);
      }
    }
    if (showShareMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShareMenu]);

  // Add animation styles to document
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.innerHTML = fadeInAnimation;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <DashboardLayout>
      <div className="refer-earn-page">
        {/* Top Section */}
        <div className="refer-earn-header">
          <h2 className="refer-earn-title">Visibeen Affiliate Program</h2>
          <p className="refer-earn-desc">
            Join the Visibeen Affiliate Program and turn your network into a source of income while empowering businesses to get noticed, chosen, and successful online.
          </p>
          <div className="refer-earn-image-container" style={{ marginTop: "20px" }}>
            <img 
              src={referAndEarnImage} 
              alt="Refer and Earn" 
              style={{ 
                maxWidth: "100%", 
                height: "auto", 
                maxHeight: "250px", 
                display: "block", 
                margin: "0 auto" 
              }} 
            />
          </div>
        </div>

        {/* Share Box */}
        <div className="refer-earn-share-box" style={{ position: "relative" }}>
          <h3 className="refer-earn-share-title">Share Your Affiliate Link</h3>
          <p className="refer-earn-share-desc">
            Share your unique affiliate link with business owners, friends, or your network via email, social media, or by copying the link. The more you share, the more you earn!
          </p>
          <div className="refer-earn-share-row" style={{ position: "relative" }}>
            <input
              className="refer-earn-link"
              value={referralLink}
              readOnly={!isEditable}
              placeholder="www.website.com"
              onClick={() => setIsEditable(true)}
              onChange={(e) => setReferralLink(e.target.value)}
            />
            <button
              className="btn btn-primary refer-earn-share-btn"
              ref={shareBtnRef}
              onClick={() => setShowShareMenu((v) => !v)}
              style={{
                borderRadius: "0 6px 6px 0",
                padding: "12px 28px",
                fontSize: "1rem",
                fontWeight: "600",
                background: showShareMenu ? "#0077c2" : "#0090e7",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                transition: "background 0.2s",
                position: "relative",
                zIndex: 10
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#0077c2"}
              onMouseLeave={(e) => e.currentTarget.style.background = showShareMenu ? "#0077c2" : "#0090e7"}
            >
              Share
            </button>
          </div>
          {showShareMenu && (
            <div className="preview-share-menu" ref={shareMenuRef} style={{
              position: "absolute",
              top: "100%",
              right: "0",
              marginTop: "5px",
              zIndex: 100,
              boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              animation: "fadeIn 0.2s ease-in-out"
            }}>
              <div 
                className="share-menu-item" 
                onClick={() => handleShare('email')} 
                style={{ padding: "10px 16px", transition: "background-color 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f2f8fd"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
              >
                <AiOutlineMail style={{ marginRight: 8 }} />
                Email
              </div>
              <div 
                className="share-menu-item" 
                onClick={() => handleShare('facebook')} 
                style={{ padding: "10px 16px", transition: "background-color 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f2f8fd"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
              >
                <FaFacebook style={{ marginRight: 8, color: "#1877f3" }} />
                Facebook
              </div>
              <div 
                className="share-menu-item" 
                onClick={() => handleShare('whatsapp')} 
                style={{ padding: "10px 16px", transition: "background-color 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f2f8fd"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
              >
                <FaWhatsapp style={{ marginRight: 8, color: "#25d366" }} />
                WhatsApp
              </div>
              <div 
                className="share-menu-item" 
                onClick={() => handleShare('instagram')} 
                style={{ padding: "10px 16px", transition: "background-color 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f2f8fd"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
              >
                <FaInstagram style={{ marginRight: 8, color: "#e4405f" }} />
                Instagram
              </div>
              <div 
                className="share-menu-item" 
                onClick={() => handleShare('copy')} 
                style={{ padding: "10px 16px", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px", transition: "background-color 0.2s" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f2f8fd"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
              >
                <AiOutlineLink style={{ marginRight: 8 }} />
                Copy link
              </div>
            </div>
          )}
        </div>

        {/* How It Works */}
        <div className="refer-earn-how-it-works">
          <h3>How It Works</h3>
          <ol>
            <li>
              <b>Sign Up</b>
              <br />
              Create your free affiliate account with Visibeen.
            </li>
            <li>
              <b>Share Your Link</b>
              <br />
              Share your unique referral link with business owners, friends, or your network.
            </li>
            <li>
              <b>Get Rewarded</b>
              <br />
              Earn commission for every successful subscription purchased through your link.
            </li>
            <li>
              <b>Track Your Earnings</b>
              <br />
              Monitor your referrals and earnings in real-time via your affiliate dashboard.
            </li>
          </ol>
        </div>

        {/* Who Can Join */}
        <div className="refer-earn-who-can-join">
          <h3>Who Can Join?</h3>
          <ul>
            <li>Digital marketers, SEO consultants, or agencies who want to offer Visibeen as part of their services.</li>
            <li>Business owners who want to help peers improve their online visibility.</li>
            <li>Influencers, bloggers, or anyone with a network of local businesses.</li>
          </ul>
          <p><strong>Everyone who shares value deserves rewards.</strong></p>
        </div>

        {/* Why You'll Love Being a Visibeen Affiliate */}
        <div className="refer-earn-benefits">
          <h3>Why You'll Love Being a Visibeen Affiliate</h3>
          <ul>
            <li><b>Simple Setup</b> – No technical knowledge required.</li>
            <li><b>High Conversion</b> – Our platform's effectiveness makes it easy for referrals to convert.</li>
            <li><b>Regular Payouts</b> – Earn consistently and receive payouts on time.</li>
            <li><b>Support & Resources</b> – Access marketing material, demos, and guidance to help you succeed.</li>
          </ul>
        </div>

        {/* Start Earning Today */}
        <div className="refer-earn-cta">
          <h3>Start Earning Today</h3>
          <p>
            Join the Visibeen Affiliate Program and turn your network into a source of income while empowering businesses to get noticed, chosen, and successful online.
          </p>
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button 
              className="btn btn-primary"
              style={{
                backgroundColor: "#0090e7",
                color: "#fff",
                border: "none",
                padding: "12px 32px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#0077c2"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#0090e7"}
            >
              Become an Affiliate
            </button>
          </div>
        </div>

        {/* Contact Us */}
        <div className="refer-earn-contact">
          <h4>Contact Us</h4>
          <p>
            If you have questions about our Affiliate Program, feel free to contact us at{" "}
            <a href="mailto:support@visibeen.com">support@visibeen.com</a>.
          </p>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </DashboardLayout>
  );
}